import {
  HOMING_UNLOCK_STACKS,
  HOMING_RANGE_M,
  MAX_HULL_SHRED,
  MAX_SLOW_FACTOR,
  WEAPON_MODS,
  WEAPON_MOD_LIST,
  encodeWeaponModOffer,
  weaponModById,
  type WeaponModElement,
  type WeaponModId,
} from "../content/weapon-mods";
import type { WeaponSlot } from "../content/schemas";
import { clamp, dist2, fromAngle, len2, normalize2 } from "./math";

export type WeaponModInstance = {
  modId: WeaponModId;
  slot: WeaponSlot;
  stacks: number;
};

/** Aggregated fire/hit behavior for one equipped slot. */
export type SlotModEffects = {
  elements: WeaponModElement[];
  /** Dominant element for projectile tint (highest stacks). */
  primaryElement: WeaponModElement | null;
  burnDps: number;
  burnDurationS: number;
  slowFactor: number;
  slowDurationS: number;
  hullShred: number;
  shredDurationS: number;
  shieldDamageBonus: number;
  shieldShred: number;
  ionDurationS: number;
  shockDurationS: number;
  shrapnelCount: number;
  shrapnelDamageFrac: number;
  countDelta: number;
  spreadDelta: number;
  cooldownMult: number;
  damageMult: number;
  pierceCount: number;
  pierceDamageRetention: number;
  ricochetCount: number;
  ricochetRangeM: number;
  ricochetDamageRetention: number;
  blastRadiusBonusM: number;
  blastDamageRetention: number;
  impactFragmentCount: number;
  impactFragmentDamageRetention: number;
  impactFragmentSpeed: number;
  crossSlotCooldownReduction: number;
  shieldLeechFraction: number;
  returnAfterS: number;
  returnDamageRetention: number;
  counterpulseProjectileClear: boolean;
  homing: boolean;
  homingTurnRate: number;
  homingRangeM: number;
  /** Total payload stacks (for HUD / homing breakpoint). */
  payloadStacks: number;
  mods: { modId: WeaponModId; stacks: number; chip: string; element: WeaponModElement | null }[];
};

export function emptySlotEffects(): SlotModEffects {
  return {
    elements: [],
    primaryElement: null,
    burnDps: 0,
    burnDurationS: 0,
    slowFactor: 0,
    slowDurationS: 0,
    hullShred: 0,
    shredDurationS: 0,
    shieldDamageBonus: 0,
    shieldShred: 0,
    ionDurationS: 0,
    shockDurationS: 0,
    shrapnelCount: 0,
    shrapnelDamageFrac: 0.28,
    countDelta: 0,
    spreadDelta: 0,
    cooldownMult: 1,
    damageMult: 1,
    pierceCount: 0,
    pierceDamageRetention: 1,
    ricochetCount: 0,
    ricochetRangeM: 0,
    ricochetDamageRetention: 1,
    blastRadiusBonusM: 0,
    blastDamageRetention: 1,
    impactFragmentCount: 0,
    impactFragmentDamageRetention: 1,
    impactFragmentSpeed: 0,
    crossSlotCooldownReduction: 0,
    shieldLeechFraction: 0,
    returnAfterS: 0,
    returnDamageRetention: 0,
    counterpulseProjectileClear: false,
    homing: false,
    homingTurnRate: 0,
    homingRangeM: HOMING_RANGE_M,
    payloadStacks: 0,
    mods: [],
  };
}

export function stacksForMod(
  instances: readonly WeaponModInstance[],
  modId: WeaponModId,
  slot: WeaponSlot,
): number {
  return instances
    .filter((m) => m.modId === modId && m.slot === slot)
    .reduce((sum, m) => sum + m.stacks, 0);
}

export function payloadStacksForSlot(
  instances: readonly WeaponModInstance[],
  slot: WeaponSlot,
): number {
  return instances
    .filter((m) => m.slot === slot && WEAPON_MODS[m.modId]?.kind === "payload")
    .reduce((sum, m) => sum + m.stacks, 0);
}

/** Resolve stacked mod effects for a weapon slot. Homing unlocks at 3 payload stacks. */
export function resolveSlotModEffects(
  instances: readonly WeaponModInstance[],
  slot: WeaponSlot,
): SlotModEffects {
  const fx = emptySlotEffects();
  const onSlot = instances.filter((m) => m.slot === slot);
  if (onSlot.length === 0) return fx;

  const elementStacks = new Map<WeaponModElement, number>();

  for (const inst of onSlot) {
    const def = WEAPON_MODS[inst.modId];
    if (!def || inst.stacks <= 0) continue;
    const s = inst.stacks;
    const c = def.curve;
    fx.mods.push({
      modId: def.id,
      stacks: s,
      chip: def.chip,
      element: def.element,
    });

    if (def.kind === "payload" && def.element) {
      fx.payloadStacks += s;
      elementStacks.set(def.element, (elementStacks.get(def.element) ?? 0) + s);
      if (!fx.elements.includes(def.element)) fx.elements.push(def.element);
    }

    if (c.burnDpsPerStack) {
      fx.burnDps += c.burnDpsPerStack * s;
      fx.burnDurationS = Math.max(fx.burnDurationS, c.burnDurationS ?? 0);
    }
    if (c.slowFactorPerStack) {
      fx.slowFactor = Math.min(MAX_SLOW_FACTOR, fx.slowFactor + c.slowFactorPerStack * s);
      fx.slowDurationS = Math.max(fx.slowDurationS, (c.slowDurationS ?? 0) + 0.15 * (s - 1));
    }
    if (c.hullShredPerStack) {
      fx.hullShred = Math.min(MAX_HULL_SHRED, fx.hullShred + c.hullShredPerStack * s);
      fx.shredDurationS = Math.max(fx.shredDurationS, c.shredDurationS ?? 0);
    }
    if (c.shieldDamageBonusPerStack) {
      fx.shieldDamageBonus += c.shieldDamageBonusPerStack * s;
      fx.ionDurationS = Math.max(fx.ionDurationS, c.ionDurationS ?? 0);
    }
    if (c.shieldShredPerStack) {
      fx.shieldShred = Math.min(0.5, fx.shieldShred + c.shieldShredPerStack * s);
    }
    if (c.shockDurationS) {
      fx.shockDurationS = Math.max(fx.shockDurationS, c.shockDurationS * s);
    }
    if (c.shrapnelCountPerStack) {
      fx.shrapnelCount += Math.floor(c.shrapnelCountPerStack * s);
      fx.shrapnelDamageFrac = c.shrapnelDamageFrac ?? fx.shrapnelDamageFrac;
    }
    if (c.countDeltaPerStack) fx.countDelta += c.countDeltaPerStack * s;
    if (c.spreadDeltaPerStack) fx.spreadDelta += c.spreadDeltaPerStack * s;
    if (c.cooldownMultPerStack) fx.cooldownMult *= c.cooldownMultPerStack ** s;
    if (c.damageMultPerStack) fx.damageMult *= c.damageMultPerStack ** s;
    if (c.pierceCountPerStack) {
      fx.pierceCount += Math.floor(c.pierceCountPerStack * s);
      fx.pierceDamageRetention = Math.min(fx.pierceDamageRetention, c.pierceDamageRetention ?? 1);
    }
    if (c.ricochetCountPerStack) {
      fx.ricochetCount += Math.floor(c.ricochetCountPerStack * s);
      fx.ricochetRangeM = Math.max(fx.ricochetRangeM, c.ricochetRangeM ?? 0);
      fx.ricochetDamageRetention = Math.min(
        fx.ricochetDamageRetention,
        c.ricochetDamageRetention ?? 1,
      );
    }
    if (c.blastRadiusMPerStack) {
      fx.blastRadiusBonusM += c.blastRadiusMPerStack * s;
      fx.blastDamageRetention = Math.min(fx.blastDamageRetention, c.blastDamageRetention ?? 1);
    }
    if (c.impactFragmentCountPerStack) {
      fx.impactFragmentCount += Math.floor(c.impactFragmentCountPerStack * s);
      fx.impactFragmentDamageRetention = Math.min(
        fx.impactFragmentDamageRetention,
        c.impactFragmentDamageRetention ?? 1,
      );
      fx.impactFragmentSpeed = Math.max(fx.impactFragmentSpeed, c.impactFragmentSpeed ?? 0);
    }
    if (c.crossSlotCooldownReductionPerStack) {
      fx.crossSlotCooldownReduction = Math.min(
        0.9,
        fx.crossSlotCooldownReduction + c.crossSlotCooldownReductionPerStack * s,
      );
    }
    if (c.shieldLeechFractionPerStack) {
      fx.shieldLeechFraction = Math.min(
        0.4,
        fx.shieldLeechFraction + c.shieldLeechFractionPerStack * s,
      );
    }
    if (c.projectileReturnAfterS) {
      fx.returnAfterS =
        fx.returnAfterS > 0
          ? Math.min(fx.returnAfterS, c.projectileReturnAfterS)
          : c.projectileReturnAfterS;
      fx.returnDamageRetention = Math.max(
        fx.returnDamageRetention,
        c.projectileReturnDamageRetention ?? 1,
      );
    }
    if (c.counterpulseProjectileClear) fx.counterpulseProjectileClear = true;
    if (c.homingTurnRatePerStack && def.kind === "payload") {
      fx.homingTurnRate += c.homingTurnRatePerStack * s;
      fx.homingRangeM = Math.max(fx.homingRangeM, def.homingRangeM);
    }
  }

  let bestEl: WeaponModElement | null = null;
  let bestStacks = 0;
  for (const [el, n] of elementStacks) {
    if (n > bestStacks) {
      bestStacks = n;
      bestEl = el;
    }
  }
  fx.primaryElement = bestEl;
  fx.homing = fx.payloadStacks >= HOMING_UNLOCK_STACKS;
  // Soft-seek only after unlock; scale with stacks at/above the breakpoint.
  if (!fx.homing) {
    fx.homingTurnRate = 0;
  } else {
    const scale = fx.payloadStacks / HOMING_UNLOCK_STACKS;
    fx.homingTurnRate *= scale;
  }

  return fx;
}

export function canStackWeaponMod(
  instances: readonly WeaponModInstance[],
  modId: WeaponModId,
  slot: WeaponSlot,
): boolean {
  const def = weaponModById(modId);
  if (!def) return false;
  return stacksForMod(instances, modId, slot) < def.maxStacks;
}

export function applyWeaponModStack(
  instances: WeaponModInstance[],
  modId: WeaponModId,
  slot: WeaponSlot,
): boolean {
  if (!canStackWeaponMod(instances, modId, slot)) return false;
  const existing = instances.find((m) => m.modId === modId && m.slot === slot);
  if (existing) {
    existing.stacks += 1;
  } else {
    instances.push({ modId, slot, stacks: 1 });
  }
  return true;
}

/** Build offer tokens for equipped slots that can still stack the mod. */
export function eligibleWeaponModOffers(
  instances: readonly WeaponModInstance[],
  equippedSlots: readonly WeaponSlot[],
  pilotRank = 1,
  blueprints: readonly string[] = [],
): string[] {
  const offers: string[] = [];
  for (const def of WEAPON_MOD_LIST) {
    if ((def.minPilotRank ?? 1) > pilotRank) continue;
    if (def.requiresBlueprint && !blueprints.includes(def.requiresBlueprint)) continue;
    const slots =
      def.preferredSlot === "any"
        ? equippedSlots
        : equippedSlots.includes(def.preferredSlot)
          ? [def.preferredSlot]
          : [];
    for (const slot of slots) {
      if (canStackWeaponMod(instances, def.id, slot)) {
        offers.push(encodeWeaponModOffer(def.id, slot));
      }
    }
  }
  return offers;
}

export type StatusTarget = {
  burnTimer: number;
  burnDps: number;
  slowTimer: number;
  slowFactor: number;
  shredTimer: number;
  hullShred: number;
  ionTimer: number;
  shieldWeakened: number;
  shockTimer: number;
  telegraphTimer: number;
  fireCooldown: number;
  shield: number;
};

/** Apply payload status from a hit. Mutates enemy status fields. */
export function applyPayloadOnHit(enemy: StatusTarget, fx: SlotModEffects): void {
  if (fx.burnDps > 0) {
    enemy.burnDps = Math.max(enemy.burnDps, fx.burnDps);
    enemy.burnTimer = Math.max(enemy.burnTimer, fx.burnDurationS);
  }
  if (fx.slowFactor > 0) {
    enemy.slowFactor = Math.max(enemy.slowFactor, fx.slowFactor);
    enemy.slowTimer = Math.max(enemy.slowTimer, fx.slowDurationS);
  }
  if (fx.hullShred > 0) {
    enemy.hullShred = Math.max(enemy.hullShred, fx.hullShred);
    enemy.shredTimer = Math.max(enemy.shredTimer, fx.shredDurationS);
  }
  if (fx.shieldShred > 0 && enemy.shield > 0) {
    enemy.shield = Math.max(0, enemy.shield * (1 - fx.shieldShred));
    enemy.shieldWeakened = Math.max(enemy.shieldWeakened, fx.shieldDamageBonus);
    enemy.ionTimer = Math.max(enemy.ionTimer, fx.ionDurationS);
  } else if (fx.shieldDamageBonus > 0) {
    enemy.shieldWeakened = Math.max(enemy.shieldWeakened, fx.shieldDamageBonus);
    enemy.ionTimer = Math.max(enemy.ionTimer, fx.ionDurationS);
  }
  if (fx.shockDurationS > 0) {
    enemy.shockTimer = Math.max(enemy.shockTimer, fx.shockDurationS);
    enemy.telegraphTimer = 0;
    enemy.fireCooldown = Math.max(enemy.fireCooldown, fx.shockDurationS);
  }
}

/** Scale incoming damage for ion (shields) and corrosive (hull). */
export function scaleDamageForPayload(
  damage: number,
  shieldBefore: number,
  fx: SlotModEffects,
  enemy: Pick<StatusTarget, "hullShred" | "shieldWeakened" | "ionTimer">,
): { againstShield: number; againstHull: number } {
  let remaining = damage;
  let againstShield = 0;
  if (shieldBefore > 0) {
    const ionBonus = 1 + fx.shieldDamageBonus + (enemy.ionTimer > 0 ? enemy.shieldWeakened : 0);
    const shieldHit = remaining * ionBonus;
    againstShield = Math.min(shieldBefore, shieldHit);
    // Convert absorbed shield HP back to "spent" damage budget.
    remaining = Math.max(0, remaining - againstShield / ionBonus);
  }
  const shred = 1 + Math.max(fx.hullShred, enemy.hullShred);
  const againstHull = remaining * shred;
  return { againstShield, againstHull };
}

export function tickEnemyStatuses(enemy: StatusTarget & { hull: number }, dt: number): number {
  let burnDamage = 0;
  if (enemy.burnTimer > 0) {
    enemy.burnTimer -= dt;
    burnDamage = enemy.burnDps * dt;
    if (enemy.burnTimer <= 0) {
      enemy.burnTimer = 0;
      enemy.burnDps = 0;
    }
  }
  if (enemy.slowTimer > 0) {
    enemy.slowTimer -= dt;
    if (enemy.slowTimer <= 0) {
      enemy.slowTimer = 0;
      enemy.slowFactor = 0;
    }
  }
  if (enemy.shredTimer > 0) {
    enemy.shredTimer -= dt;
    if (enemy.shredTimer <= 0) {
      enemy.shredTimer = 0;
      enemy.hullShred = 0;
    }
  }
  if (enemy.ionTimer > 0) {
    enemy.ionTimer -= dt;
    if (enemy.ionTimer <= 0) {
      enemy.ionTimer = 0;
      enemy.shieldWeakened = 0;
    }
  }
  if (enemy.shockTimer > 0) {
    enemy.shockTimer -= dt;
    if (enemy.shockTimer <= 0) enemy.shockTimer = 0;
  }
  return burnDamage;
}

export type HomingTarget = { id: number; x: number; y: number };

/** Soft-seek: rotate velocity toward nearest target within lock range. */
export function steerProjectileHoming(
  shot: { x: number; y: number; vx: number; vy: number },
  targets: readonly HomingTarget[],
  turnRate: number,
  rangeM: number,
  dt: number,
): void {
  if (turnRate <= 0 || rangeM <= 0 || targets.length === 0) return;
  const speed = len2({ x: shot.vx, y: shot.vy });
  if (speed < 1) return;

  let best: HomingTarget | null = null;
  let bestDist = rangeM;
  for (const t of targets) {
    const d = dist2(shot, t);
    if (d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  if (!best) return;

  const desired = normalize2({ x: best.x - shot.x, y: best.y - shot.y });
  const currentAngle = Math.atan2(shot.vy, shot.vx);
  const desiredAngle = Math.atan2(desired.y, desired.x);
  let delta = desiredAngle - currentAngle;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  const maxTurn = turnRate * dt;
  const turned = currentAngle + clamp(delta, -maxTurn, maxTurn);
  const dir = fromAngle(turned);
  shot.vx = dir.x * speed;
  shot.vy = dir.y * speed;
}

export type ShrapnelSpec = {
  count: number;
  damage: number;
  speed: number;
  life: number;
  radius: number;
};

export function shrapnelSpecFromKill(
  fx: SlotModEffects,
  killDamage: number,
  baseSpeed = 55,
): ShrapnelSpec | null {
  if (fx.shrapnelCount <= 0) return null;
  return {
    count: fx.shrapnelCount,
    damage: killDamage * fx.shrapnelDamageFrac,
    speed: baseSpeed,
    life: 0.45,
    radius: 0.55,
  };
}

export function shrapnelDirections(count: number, baseAngle = 0): { x: number; y: number }[] {
  const dirs: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i += 1) {
    const a = baseAngle + (i * Math.PI * 2) / count;
    dirs.push(fromAngle(a));
  }
  return dirs;
}

export { HOMING_UNLOCK_STACKS, HOMING_RANGE_M };
